import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Sun, Globe } from "lucide-react";
import { MaskedLines, FadeIn, ParallaxImage } from "@/components/motion";
import { IMAGES, VALUES, TEAM_LEADERS, TEAM_COORDINATORS, STATS, usePageMeta } from "@/lib/site";

const VALUE_ICONS = [Heart, Users, Sun, Globe];

const STORY = [
  {
    number: "01",
    title: "Where we began",
    text: "Hope Centers International was born in El Paso, Texas, with a simple conviction: no child should be hungry or unschooled while someone, somewhere, could help. That conviction carried us to the Philippines — and later to Lithuania — where we opened our first feeding centers.",
  },
  {
    number: "02",
    title: "What we believe",
    text: "We stand in the gap for children in need by feeding and educating them. We believe a meal served with dignity can change a day, an education can change a life, and a community that stays can change everything.",
  },
  {
    number: "03",
    title: "Where we're going",
    text: "More centers, more students, more neighborhoods — each one led by local leaders and built to last. The dream is simple: a table and a classroom within reach of every child we serve.",
  },
];

const About = () => {
  usePageMeta(
    "About — Hope Centers International",
    "Our story, mission, values, and the team behind Hope Centers International."
  );

  return (
    <div data-testid="about-page">
      <section className="pt-32 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn y={16}>
            <p className="label">Our story</p>
          </FadeIn>
          <h1
            data-testid="about-heading"
            className="mt-6 font-serif font-medium text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] tracking-tighter leading-[0.98]"
          >
            <MaskedLines delay={0.15} lines={[<>Hope is a</>, <><em className="text-terra">daily practice.</em></>]} />
          </h1>
          <FadeIn delay={0.5} className="mt-8 max-w-xl">
            <p className="text-base sm:text-lg leading-relaxed text-ink/75">
              From serving our community in New Mexico to twenty-seven centers across two countries — this is who we are and why
              we stay.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* STORY CHAPTERS */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-16 md:space-y-24">
          {STORY.map((c, i) => (
            <FadeIn key={c.number}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-start border-t border-ink/10 pt-10 md:pt-14">
                <div className="md:col-span-4">
                  <span
                    className="font-serif text-7xl md:text-8xl font-semibold leading-none"
                    style={{ WebkitTextStroke: "1.5px #EC4849", color: "transparent" }}
                  >
                    {c.number}
                  </span>
                </div>
                <div className="md:col-span-7 md:col-start-6">
                  <h2 className="font-serif text-3xl md:text-5xl font-medium tracking-tight">{c.title}</h2>
                  <p className="mt-5 text-base sm:text-lg leading-relaxed text-ink/70 max-w-xl">{c.text}</p>
                </div>
              </div>
            </FadeIn>
          ))}
          <FadeIn>
            <ParallaxImage
              src={IMAGES.about}
              alt="Children at a Hope Centers program peeking into the frame and smiling"
              className="rounded-2xl aspect-[16/9] lg:aspect-[21/9]"
              speed={0.08}
            />
          </FadeIn>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 md:py-32 bg-paper-deep border-y border-ink/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn className="mb-14">
            <p className="label">What we value</p>
            <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl tracking-tight leading-[1.08]">
              Four things we won't compromise.
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {VALUES.map((v, i) => {
              const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
              return (
                <FadeIn key={v.title} delay={i * 0.1}>
                  <div data-testid={`about-value-${i}`} className="h-full bg-white border border-ink/10 rounded-2xl p-8">
                    <span className="grid place-items-center w-12 h-12 rounded-full bg-marigold/20 text-marigold">
                      <Icon size={20} />
                    </span>
                    <h3 className="mt-6 font-serif text-2xl font-medium tracking-tight">{v.title}</h3>
                    <p className="mt-3 text-ink/65 leading-relaxed text-sm">{v.text}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn className="mb-14">
            <p className="label">Leadership</p>
            <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl tracking-tight leading-[1.08]">
              The people behind the plates.
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {TEAM_LEADERS.map((t, i) => (
              <FadeIn key={t.name} delay={i * 0.1}>
                <div data-testid={`about-leader-${i}`} className="group">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-paper-deep border border-ink/10">
                    <img
                      src={t.photo}
                      alt={`Portrait of ${t.name}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="mt-5 font-serif text-2xl font-medium tracking-tight">{t.name}</h3>
                  <p className="mt-1 text-sm text-ink-soft">{t.role}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn className="mt-20 mb-10">
            <div className="flex items-center gap-6">
              <h3 className="font-serif text-2xl md:text-3xl font-medium tracking-tight shrink-0">
                Field coordinators
              </h3>
              <span className="h-px flex-1 bg-ink/10" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-ink-soft shrink-0">
                Philippines · Lithuania · Japan
              </span>
            </div>
          </FadeIn>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {TEAM_COORDINATORS.map((t, i) => (
              <FadeIn key={t.name} delay={(i % 4) * 0.08}>
                <div data-testid={`about-coordinator-${i}`} className="group">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-paper-deep border border-ink/10">
                    <img
                      src={t.photo}
                      alt={`Portrait of ${t.name}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <h4 className="mt-4 font-serif text-lg md:text-xl font-medium tracking-tight leading-snug">
                    {t.name}
                  </h4>
                  <p className="mt-0.5 text-xs text-ink-soft">{t.role}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT STRIP + CTA */}
      <section className="bg-forest text-paper py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-paper/50">By the numbers</p>
          </FadeIn>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
            {STATS.map((s, i) => (
              <FadeIn key={s.label} delay={i * 0.1}>
                <p className="font-serif text-6xl md:text-7xl font-medium tracking-tight text-marigold">{s.value}</p>
                <p className="mt-3 text-sm text-paper/65 leading-relaxed max-w-[180px]">{s.label}</p>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <div className="mt-16 md:mt-24 border-t border-paper/15 pt-12 flex flex-col md:flex-row md:items-end justify-between gap-8">
              <h2 className="font-serif font-medium text-4xl sm:text-5xl tracking-tight leading-[1.08] max-w-lg">
                The next chapter needs <em className="text-marigold">you.</em>
              </h2>
              <Link
                to="/donate"
                data-testid="about-cta-donate-button"
                className="shrink-0 inline-flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-bold text-paper transition-[background-color,transform] duration-300 hover:bg-terra-dark hover:scale-105"
              >
                Donate
                <ArrowRight size={16} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default About;
